import React, { useState, useMemo } from 'react';
import { Card, Button, Input } from '../ui';
import { CheckSquare, AlertTriangle, FileText, CheckCircle2, ChevronRight, RefreshCw } from 'lucide-react';

interface PolicyCheck {
  id: string;
  label: string;
  points: number;
  tip: string;
}

interface Grant {
  id: string;
  name: string;
  funder: string;
  amount: string;
  reqs: string;
  minScore: number;
}

const GRANT_DATABASE: Grant[] = [
  { id: 'g1', name: 'Youth Athletics Improvement Grant', funder: 'Nike Foundation', amount: '$5,000 - $10,000', reqs: 'Requires active athletic program & 501(c)(3) status.', minScore: 70 },
  { id: 'g2', name: 'STEM & Music Booster Support Grant', funder: 'Chevron Corporate Giving', amount: '$2,500', reqs: 'Requires active bylaws, budget policy, and school charter certification.', minScore: 50 },
  { id: 'g3', name: 'Community Youth Development Fund', funder: 'Target Giving Network', amount: '$1,000 - $5,000', reqs: 'Requires 501(c)(3) registration & active community volunteer logs.', minScore: 40 },
  { id: 'g4', name: 'Mega Booster League Grant', funder: 'State Sports Coalition', amount: '$20,000', reqs: 'Requires a minimum audit check score of 90, board conflicts-of-interest policy, and $5k+ financial reserve.', minScore: 90 }
];

const COMPLIANCE_ITEMS: PolicyCheck[] = [
  { id: '501c3', label: 'Registered as 501(c)(3) Nonprofit', points: 30, tip: 'Unlocks local corporate matching grants and tax-deductible donations.' },
  { id: 'bylaws', label: 'Adopted Written Corporate Bylaws', points: 15, tip: 'Most foundations demand a signed copy of bylaws to verify governance structure.' },
  { id: 'conflict', label: 'Conflict of Interest Policy Adopted', points: 15, tip: 'Required for IRS 990 filings and mega corporate grants.' },
  { id: 'budget', label: 'Annual Budget Formally Approved by Board', points: 20, tip: 'Protects board members and shows financial oversight compliance.' },
  { id: 'school', label: 'Signed MOU/Agreement with School Board', points: 10, tip: 'Validates booster organization authority to fundraise under school identity.' },
  { id: 'reserve', label: 'Financial Reserve Buffer (At least 15% of annual budget)', points: 10, tip: 'Proves operational continuity and safeguards camp/equipment reservations.' }
];

export function BoosterGrantScanner() {
  const [checkedIds, setCheckedIds] = useState<string[]>(['school']);
  const [annualBudget, setAnnualBudget] = useState('15000');
  const [isScanned, setIsScanned] = useState(false);

  const handleToggle = (id: string) => {
    setCheckedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const complianceScore = useMemo(() => {
    return COMPLIANCE_ITEMS.reduce((score, item) => {
      return score + (checkedIds.includes(item.id) ? item.points : 0);
    }, 0);
  }, [checkedIds]);

  const matchingGrants = useMemo(() => {
    return GRANT_DATABASE.filter(g => complianceScore >= g.minScore);
  }, [complianceScore]);

  const loadSample = () => {
    setCheckedIds(['501c3', 'bylaws', 'budget', 'school']);
    setAnnualBudget('20000');
    setIsScanned(true);
  };

  return (
    <div className="space-y-8">
      {/* Intro Hero */}
      <div className="bg-[#FFD600] border-4 border-black p-6 shadow-[4px_4px_0px_#000000] text-black">
        <h3 className="font-display font-black text-2xl uppercase tracking-tight mb-2">Booster Club Grant Eligibility Scanner</h3>
        <p className="font-bold text-sm max-w-xl">
          Audit your school booster club (Athletics, Band, PTO) against standard corporate grant criteria. Instantly discover matches.
        </p>
        <div className="flex gap-3 mt-4">
          <Button onClick={loadSample} variant="secondary" className="bg-black text-white hover:bg-[#0044FF] border-2 border-black rounded-none uppercase font-mono tracking-widest text-[10px] font-bold">
            <RefreshCw size={12} className="mr-2" /> Load Demo
          </Button>
          <Button onClick={() => { setCheckedIds([]); setIsScanned(false); }} variant="ghost" className="border-2 border-black text-black hover:bg-red-600 hover:text-white rounded-none uppercase font-mono tracking-widest text-[10px] font-bold">
            Reset
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        
        {/* Compliance Checklist */}
        <div className="lg:col-span-1 bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4">
          <h4 className="font-display font-black text-xl uppercase tracking-tight">Audit Checklist</h4>
          <p className="text-xs text-gray-500">Check the items currently in place for your organization.</p>
          
          <div className="space-y-4 pt-2">
            {COMPLIANCE_ITEMS.map(item => {
              const isChecked = checkedIds.includes(item.id);
              return (
                <div 
                  key={item.id}
                  onClick={() => handleToggle(item.id)}
                  className={`border-2 border-black p-3 cursor-pointer select-none transition-all ${
                    isChecked ? 'bg-[#FFD600]/25 border-[#0044FF]' : 'bg-[#F5F5F0] hover:bg-gray-100 dark:bg-slate-900'
                  }`}
                >
                  <div className="flex items-start gap-2.5">
                    <input 
                      type="checkbox" 
                      checked={isChecked} 
                      readOnly 
                      className="w-4 h-4 text-[#0044FF] border-2 border-black rounded-none focus:ring-0 mt-0.5" 
                    />
                    <div>
                      <span className="text-xs font-black uppercase tracking-tight block text-black dark:text-white">{item.label}</span>
                      <span className="text-[10px] font-mono text-gray-500 block mt-1 leading-normal">{item.tip}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="pt-4 border-t-2 border-black">
             <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Annual Booster Budget ($)</label>
             <Input 
               type="number" 
               value={annualBudget} 
               onChange={e => setAnnualBudget(e.target.value)} 
               className="border-2 border-black rounded-none h-10 font-mono font-bold" 
             />
          </div>

          <Button 
            onClick={() => setIsScanned(true)} 
            className="w-full bg-[#0044FF] text-white border-2 border-black rounded-none font-bold uppercase tracking-wider py-3 hover:bg-black"
          >
            Scan Grant Matches
          </Button>
        </div>

        {/* Scan Outputs */}
        <div className="lg:col-span-2 space-y-6">
          
          {!isScanned ? (
            <Card className="h-full min-h-[350px] flex flex-col items-center justify-center text-center p-8 bg-[#F5F5F0] border-dashed border-4 border-black dark:bg-slate-950">
              <FileText className="w-16 h-16 text-gray-400 mb-4" />
              <h3 className="text-2xl font-display font-black uppercase text-gray-500">Awaiting Compliance Audit Scan</h3>
              <p className="text-gray-500 text-xs max-w-sm mt-2 font-mono">Fill out the checklist on the left and click "Scan Grant Matches" to generate eligibility results.</p>
            </Card>
          ) : (
            <div className="space-y-6 text-sm">
              
              {/* Score card */}
              <div className="border-4 border-black p-6 bg-white dark:bg-black flex flex-col sm:flex-row items-center justify-between gap-6 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
                <div>
                  <h4 className="font-display font-black text-2xl uppercase tracking-tight text-black dark:text-white">Audit Audit Score</h4>
                  <p className="text-xs text-gray-500 mt-1 max-w-md font-mono">Based on standard foundation compliance filters. Higher scores unlock bigger funding tiers.</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-20 h-20 bg-black text-white dark:bg-white dark:text-black border-4 border-black flex items-center justify-center text-3xl font-black font-mono">
                    {complianceScore}
                  </div>
                  <div>
                    <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-[#0044FF] block">Tier Rating</span>
                    <span className="text-sm font-black uppercase">
                      {complianceScore >= 80 ? 'Gold Status (Ready)' : complianceScore >= 50 ? 'Bronze Status (Fair)' : 'Improvement Required'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Matched grants */}
              <div className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4">
                <h4 className="font-display font-black text-xl uppercase tracking-tight border-b-4 border-black pb-2 dark:border-white">
                  Eligible Foundation Grants ({matchingGrants.length})
                </h4>

                {matchingGrants.length === 0 ? (
                  <div className="border-2 border-dashed border-red-500 p-4 text-center text-red-600 font-mono text-xs uppercase">
                     No matching grants. Raise your compliance score to unlock foundation listings.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {matchingGrants.map(grant => (
                      <div key={grant.id} className="border-2 border-black p-4 bg-[#F5F5F0] dark:bg-slate-900 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                          <span className="text-[9px] font-mono font-bold uppercase tracking-wider text-white bg-black dark:bg-white dark:text-black px-1.5 py-0.5">{grant.funder}</span>
                          <h5 className="font-display text-lg font-black uppercase mt-1">{grant.name}</h5>
                          <p className="text-xs text-gray-500 mt-1 leading-normal font-mono">{grant.reqs}</p>
                        </div>
                        <div className="text-left sm:text-right shrink-0">
                          <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-gray-400 block">Grant Value</span>
                          <span className="text-xl font-mono font-black text-[#0044FF]">{grant.amount}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recommendations */}
              <div className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4">
                <h4 className="font-display font-black text-xl uppercase tracking-tight border-b-4 border-black pb-2 dark:border-white">
                  Next Steps to Unlock Grants
                </h4>
                
                <div className="space-y-3">
                  {COMPLIANCE_ITEMS.filter(item => !checkedIds.includes(item.id)).map(item => (
                    <div key={item.id} className="flex gap-2.5 items-start p-3 bg-red-50 border-2 border-dashed border-red-500 dark:bg-red-950/20 text-xs">
                      <AlertTriangle className="text-red-500 mt-0.5 shrink-0" size={16} />
                      <div>
                        <span className="font-black uppercase tracking-tight block text-red-600 dark:text-red-400">Implement: {item.label}</span>
                        <p className="text-gray-600 dark:text-gray-400 mt-1 font-mono leading-normal">{item.tip}</p>
                      </div>
                    </div>
                  ))}
                  
                  {COMPLIANCE_ITEMS.filter(item => !checkedIds.includes(item.id)).length === 0 && (
                    <div className="flex gap-2.5 items-center p-3 bg-green-50 border-2 border-green-500 dark:bg-green-950/20 text-xs font-bold text-green-700">
                      <CheckCircle2 size={16} />
                      <span>Excellent! Your booster club is compliant with all evaluated foundation metrics. You are prepared to apply for top-tier corporate grants.</span>
                    </div>
                  )}
                </div>
              </div>

            </div>
          )}

        </div>

      </div>

    </div>
  );
}
